import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  Download,
  FileText,
  LifeBuoy,
  MessageCircleQuestion,
  Video,
} from 'lucide-react';
import SiteBrandedLoader from '../../components/SiteBrandedLoader';
import { HAIRLINE, Panel, ProgressRing } from '../../components/assessment/AssessmentUI';
import { PortalShell, dueLabel } from '../../components/portal/PortalShell';
import { portalService } from '../../services/api';
import { CoachingHealth, PortalDashboard as Dashboard, TaskStatus } from '../../types/portal';

const fa = (n: number) => n.toLocaleString('fa-IR');

const HEALTH: Record<CoachingHealth, { label: string; color: string }> = {
  GREEN: { label: 'در مسیر', color: '#0ca30c' },
  YELLOW: { label: 'نیازمند توجه', color: '#fab219' },
  RED: { label: 'عقب‌افتاده', color: '#d03b3b' },
};

export const STATUS: Record<TaskStatus, { label: string; color: string }> = {
  NOT_STARTED: { label: 'شروع نشده', color: '#94A3B8' },
  IN_PROGRESS: { label: 'در حال انجام', color: '#3B82C4' },
  SUBMITTED: { label: 'تحویل شده', color: '#7C3AED' },
  UNDER_REVIEW: { label: 'در حال بررسی', color: '#7C3AED' },
  NEEDS_REVISION: { label: 'نیازمند اصلاح', color: '#fab219' },
  APPROVED: { label: 'تأیید شده', color: '#0ca30c' },
  OVERDUE: { label: 'عقب‌افتاده', color: '#d03b3b' },
};

/** تسک‌هایی که کاربر باید همین حالا سراغشان برود */
const NEEDS_ACTION: TaskStatus[] = ['OVERDUE', 'NEEDS_REVISION', 'NOT_STARTED', 'IN_PROGRESS'];

const HERO_BG = 'linear-gradient(140deg,#16307F 0%,#1C3B9B 50%,#4B2FA8 100%)';
const CTA_BG = 'linear-gradient(135deg,#1C3B9B,#3A2FA0)';

const PortalDashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<Dashboard | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await portalService.getDashboard());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'دریافت اطلاعات پرتال انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFBFE]">
        <SiteBrandedLoader />
      </div>
    );
  }

  if (!data) {
    return (
      <PortalShell>
        <div className="mx-auto max-w-2xl py-10">
          <Panel className="p-10 text-center">
            <p className="text-sm font-bold text-slate-600">
              {error || 'شما در دورهٔ کوچینگ ثبت‌نام نشده‌اید.'}
            </p>
            <Link
              to="/dashboard"
              className="mt-4 inline-block rounded-2xl px-5 py-3 text-sm font-black text-white"
              style={{ background: CTA_BG }}
            >
              بازگشت به داشبورد
            </Link>
          </Panel>
        </div>
      </PortalShell>
    );
  }

  const health = HEALTH[data.participant.health];
  const actionable = data.assignments.filter((a) => NEEDS_ACTION.includes(a.status));
  const finished = data.assignments.filter((a) => !NEEDS_ACTION.includes(a.status));
  const mentorName =
    [data.mentor?.firstName, data.mentor?.lastName].filter(Boolean).join(' ') || 'هنوز تعیین نشده';

  /** نزدیک‌ترین مهلتِ کاری که هنوز باز است — همان چیزی که باید در هیرو دیده شود */
  const nextDue = actionable
    .filter((item) => item.dueAt)
    .sort((a, b) => new Date(a.dueAt as string).getTime() - new Date(b.dueAt as string).getTime())[0];
  const nextDueInfo = nextDue?.dueAt ? dueLabel(nextDue.dueAt) : null;

  /** رنگ فقط وقتی می‌آید که عدد معنا داشته باشد؛ وگرنه کاشی خاکستری می‌ماند */
  const tiles = [
    {
      label: 'کل تسک‌ها',
      value: data.counters.total ?? 0,
      icon: <FileText className="h-4 w-4" />,
      color: '#1C3B9B',
    },
    {
      label: 'تأیید شده',
      value: data.counters.APPROVED ?? 0,
      icon: <CheckCircle2 className="h-4 w-4" />,
      color: '#0ca30c',
    },
    {
      label: 'عقب‌افتاده',
      value: data.counters.OVERDUE ?? 0,
      icon: <AlertTriangle className="h-4 w-4" />,
      color: '#d03b3b',
    },
    {
      label: 'اعلان نخوانده',
      value: data.counters.unreadNotifications ?? 0,
      icon: <Bell className="h-4 w-4" />,
      color: '#B45309',
    },
  ];

  return (
    <PortalShell>
      <div className="space-y-4">
        <Panel className="overflow-hidden">
          <div className="px-5 py-7 text-white sm:px-8" style={{ background: HERO_BG }}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-xl font-black sm:text-2xl">{data.program.title}</h1>
                {data.program.welcomeText ? (
                  <p className="mt-2 max-w-xl text-[12.5px] leading-7 text-white/85">
                    {data.program.welcomeText}
                  </p>
                ) : null}
                <p className="mt-2.5 text-[11.5px] font-bold text-white/70">
                  {data.participant.cohort.name} · منتور شما: {mentorName}
                </p>
              </div>
              <div className="flex items-center gap-3 rounded-2xl bg-white/12 px-4 py-3">
                <ProgressRing value={data.participant.progressPercent} />
                <div>
                  <p className="text-[10.5px] font-bold text-white/70">وضعیت شما</p>
                  <p className="mt-0.5 inline-flex items-center gap-1.5 text-[13px] font-black text-white">
                    <span
                      aria-hidden
                      className="h-2 w-2 rounded-full ring-2 ring-white/30"
                      style={{ backgroundColor: health.color }}
                    />
                    {health.label}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {nextDue && nextDueInfo ? (
            <Link
              to={'/portal/tasks/' + nextDue.id}
              className="flex items-center gap-2.5 px-5 py-3 transition hover:bg-slate-50 sm:px-8"
              style={{
                backgroundColor: nextDueInfo.overdue
                  ? '#FEF2F2'
                  : nextDueInfo.soon
                    ? '#FFFBEB'
                    : '#F8FAFF',
              }}
            >
              <span
                aria-hidden
                className="h-2 w-2 shrink-0 rounded-full"
                style={{
                  backgroundColor: nextDueInfo.overdue
                    ? '#d03b3b'
                    : nextDueInfo.soon
                      ? '#fab219'
                      : '#1C3B9B',
                }}
              />
              <p className="min-w-0 flex-1 truncate text-[12px] font-black text-slate-700">
                {nextDueInfo.overdue ? 'از مهلت گذشته: ' : 'مهلت بعدی: '}
                <span className="font-bold text-slate-500">{nextDue.task.title}</span>
              </p>
              <span
                className="shrink-0 text-[11px] font-black"
                style={{
                  color: nextDueInfo.overdue
                    ? '#d03b3b'
                    : nextDueInfo.soon
                      ? '#B45309'
                      : '#64748B',
                }}
              >
                {nextDueInfo.text}
              </span>
            </Link>
          ) : null}

          <div className="grid grid-cols-2 border-t sm:grid-cols-4" style={{ borderColor: HAIRLINE }}>
            {tiles.map((tile) => {
              const active = tile.value > 0;
              return (
                <div key={tile.label} className="px-3 py-4 text-center">
                  <span
                    className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl"
                    style={{
                      backgroundColor: active ? tile.color + '14' : '#F1F5F9',
                      color: active ? tile.color : '#CBD5E1',
                    }}
                  >
                    {tile.icon}
                  </span>
                  <p
                    className="mt-1.5 text-2xl font-black tabular-nums"
                    style={{ color: active ? tile.color : '#CBD5E1' }}
                  >
                    {fa(tile.value)}
                  </p>
                  <p className="text-[10.5px] font-bold text-slate-400">{tile.label}</p>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel className="p-4 sm:p-5">
          <p className="mb-3 text-[14px] font-black text-slate-900">
            کارهای شما ({fa(actionable.length)})
          </p>
          {actionable.length ? (
            <div className="space-y-2">
              {actionable.map((item) => {
                const badge = STATUS[item.status];
                return (
                  <Link
                    key={item.id}
                    to={'/portal/tasks/' + item.id}
                    className="flex items-center gap-3 rounded-2xl border p-3 transition hover:border-[#9DB0E4]"
                    style={{ borderColor: HAIRLINE }}
                  >
                    <span
                      aria-hidden
                      className="h-9 w-1 shrink-0 rounded-full"
                      style={{ backgroundColor: badge.color }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-black text-slate-900">
                        {item.task.title}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10.5px] font-bold text-slate-400">
                        <span>{item.task.track?.title ?? 'بدون محور'}</span>
                        {(() => {
                          const label = dueLabel(item.dueAt);
                          if (!label) return null;
                          return (
                            <span
                              className="font-black"
                              style={{
                                color: label.overdue
                                  ? '#d03b3b'
                                  : label.soon
                                    ? '#B45309'
                                    : '#94A3B8',
                              }}
                            >
                              {label.text}
                            </span>
                          );
                        })()}
                      </span>
                    </span>
                    <span
                      className="shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-black"
                      style={{ backgroundColor: badge.color + '18', color: badge.color }}
                    >
                      {badge.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center">
              <CheckCircle2
                className="mx-auto h-8 w-8"
                style={{ color: data.assignments.length ? '#0ca30c' : '#CBD5E1' }}
              />
              <p className="mt-2 text-[12.5px] font-black text-slate-500">
                {data.assignments.length
                  ? 'کار باز ندارید — همهٔ تمرین‌ها تحویل شده‌اند.'
                  : 'هنوز تمرینی برای شما منتشر نشده است.'}
              </p>
              <p className="mt-1 text-[11px] font-bold text-slate-400">
                {data.assignments.length
                  ? 'به‌محض انتشار تمرین بعدی، همین‌جا و با اعلان به شما خبر می‌دهیم.'
                  : 'منتور شما به‌زودی اولین تمرین را منتشر می‌کند.'}
              </p>
            </div>
          )}

          {finished.length ? (
            <details className="mt-3">
              <summary className="cursor-pointer text-[12px] font-black text-slate-500">
                تمرین‌های تمام‌شده ({fa(finished.length)})
              </summary>
              <div className="mt-2 space-y-1.5">
                {finished.map((item) => {
                  const badge = STATUS[item.status];
                  return (
                    <Link
                      key={item.id}
                      to={'/portal/tasks/' + item.id}
                      className="flex items-center gap-2 rounded-xl border px-3 py-2 text-[12px]"
                      style={{ borderColor: HAIRLINE }}
                    >
                      <span className="min-w-0 flex-1 truncate font-bold text-slate-600">
                        {item.task.title}
                      </span>
                      <span className="shrink-0 font-black" style={{ color: badge.color }}>
                        {badge.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </details>
          ) : null}
        </Panel>

        <Panel className="p-4 sm:p-5">
          <p className="mb-1 text-[14px] font-black text-slate-900">نقشهٔ شش مسیر ۶C</p>
          <p className="mb-4 text-[11px] font-bold text-slate-400">
            پیشرفت شما در هر محور، بر اساس تمرین‌های تأییدشده.
          </p>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {data.trackProgress.map((track) => {
              const color = track.color || '#1C3B9B';
              const empty = track.total === 0;
              const complete = !empty && track.done === track.total;

              return (
                <div
                  key={track.id}
                  className="rounded-2xl border p-3.5"
                  style={{ borderColor: complete ? color + '44' : HAIRLINE }}
                >
                  <div className="flex items-start gap-2.5">
                    <span
                      aria-hidden
                      className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: empty ? '#E2E8F0' : color }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className="truncate text-[12.5px] font-black"
                          style={{ color: empty ? '#94A3B8' : '#0F172A' }}
                        >
                          {track.title}
                        </p>
                        {empty ? null : (
                          <span
                            className="shrink-0 text-[11px] font-black tabular-nums"
                            style={{ color: complete ? color : '#64748B' }}
                          >
                            {fa(track.done)}/{fa(track.total)}
                          </span>
                        )}
                      </div>

                      {empty ? (
                        <p className="mt-1.5 text-[10.5px] font-bold text-slate-300">
                          هنوز تمرینی در این محور ندارید
                        </p>
                      ) : (
                        <>
                          {/* ریل همیشه دیده می‌شود؛ نوار خالی نباید با «نبود محور» اشتباه شود */}
                          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full transition-all duration-700"
                              style={{
                                width: Math.max(track.percent, track.done ? 6 : 0) + '%',
                                backgroundColor: color,
                              }}
                            />
                          </div>
                          <p className="mt-1 text-[10px] font-bold text-slate-400">
                            {complete
                              ? 'این محور کامل شد'
                              : track.done
                                ? fa(track.total - track.done) + ' تمرین باقی مانده'
                                : 'هنوز شروع نکرده‌اید'}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel className="p-4 sm:p-5">
          <p className="mb-3 text-[14px] font-black text-slate-900">جلسات دوره</p>
          <div className="space-y-2">
            {data.sessions.map((session) => (
              <details key={session.id} className="rounded-2xl border" style={{ borderColor: HAIRLINE }}>
                <summary className="flex cursor-pointer items-center gap-3 p-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#EEF3FF] text-[12px] font-black text-[#1C3B9B]">
                    {fa(session.number)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-black text-slate-900">
                      {session.title}
                    </span>
                    <span className="text-[10.5px] font-bold text-slate-400">
                      {session.heldOn || 'تاریخ اعلام نشده'}
                      {session.materials.length
                        ? ' · ' + fa(session.materials.length) + ' فایل'
                        : ''}
                    </span>
                  </span>
                </summary>

                <div className="border-t p-3" style={{ borderColor: HAIRLINE }}>
                  {session.description ? (
                    <p className="mb-2 text-[12px] leading-7 text-slate-600">{session.description}</p>
                  ) : null}

                  {session.meetingUrl ? (
                    <a
                      href={session.meetingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mb-2 inline-flex items-center gap-1.5 rounded-xl bg-[#EEF3FF] px-3 py-2 text-[11.5px] font-black text-[#1C3B9B]"
                    >
                      <Video className="h-3.5 w-3.5" />
                      ورود به جلسهٔ آنلاین
                    </a>
                  ) : null}

                  <div className="space-y-1.5">
                    {session.materials.map((material) =>
                      material.kind === 'FILE' ? (
                        <button
                          key={material.id}
                          type="button"
                          onClick={() =>
                            portalService.downloadMaterial(
                              material.id,
                              material.originalName || material.title,
                            )
                          }
                          className="flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-right transition hover:border-[#9DB0E4]"
                          style={{ borderColor: HAIRLINE }}
                        >
                          <Download className="h-3.5 w-3.5 shrink-0 text-[#1C3B9B]" />
                          <span className="min-w-0 flex-1 truncate text-[12px] font-bold text-slate-700">
                            {material.title}
                          </span>
                        </button>
                      ) : (
                        <a
                          key={material.id}
                          href={material.url ?? '#'}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 transition hover:border-[#9DB0E4]"
                          style={{ borderColor: HAIRLINE }}
                        >
                          <Video className="h-3.5 w-3.5 shrink-0 text-[#1C3B9B]" />
                          <span className="min-w-0 flex-1 truncate text-[12px] font-bold text-slate-700">
                            {material.title}
                          </span>
                        </a>
                      ),
                    )}
                    {!session.materials.length ? (
                      <p className="py-2 text-center text-[11px] font-bold text-slate-400">
                        هنوز فایلی برای این جلسه ثبت نشده.
                      </p>
                    ) : null}
                  </div>
                </div>
              </details>
            ))}
          </div>
        </Panel>

        <div className="grid gap-3 sm:grid-cols-2">
          <Link
            to="/portal/questions"
            className="flex items-center gap-3 rounded-2xl border bg-white p-4 transition hover:border-[#9DB0E4]"
            style={{ borderColor: HAIRLINE }}
          >
            <MessageCircleQuestion className="h-5 w-5 shrink-0 text-[#1C3B9B]" />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-black text-slate-900">پرسش از منتور</span>
              <span className="text-[11px] font-bold text-slate-400">
                {fa(data.counters.openQuestions ?? 0)} سؤال باز
              </span>
            </span>
          </Link>
          <Link
            to="/portal/support"
            className="flex items-center gap-3 rounded-2xl border bg-white p-4 transition hover:border-[#9DB0E4]"
            style={{ borderColor: HAIRLINE }}
          >
            <LifeBuoy className="h-5 w-5 shrink-0 text-[#1C3B9B]" />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-black text-slate-900">پشتیبانی سایت</span>
              <span className="text-[11px] font-bold text-slate-400">ثبت تیکت و پیگیری</span>
            </span>
          </Link>
        </div>
      </div>
    </PortalShell>
  );
};

export default PortalDashboardPage;
